import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/lib/auth_context';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Cog, Sun, Moon, Monitor, Keyboard, ShieldCheck, FolderOpen, CreditCard, UserCircle, Link as LinkIcon, IdCard, Edit3, Languages, Inbox, BookOpen, BarChart2 } from 'lucide-react';
import { usePW } from '@/lib/store';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { projectsApi } from '@/lib/api';
import { ModelPicker } from './settings/ModelPicker';
import { BillingPanel } from './settings/BillingPanel';
import MyBillingPanel from './settings/MyBillingPanel';
import { readHelpMode, writeHelpMode } from '@/components/HelpTip';
import { resetTour } from '@/components/GuidedTour';
import { MyAccountPanel } from './settings/MyAccountPanel';
import ConnectionsPanel from './settings/ConnectionsPanel';
import NotebookDigestToggle from './settings/NotebookDigestToggle';
import { getPromptMode, setPromptMode, getReaskFrequency, setReaskFrequency } from '@/components/OnboardingPromptModeDialog';
import UserProfilePanel from '@/pages/UserProfilePanel';
import ByokKeysPanel from './settings/ByokKeysPanel';
import ClaritySettingsCard from './settings/ClaritySettingsCard';
import { readOpportunityBannerPref, writeOpportunityBannerPref } from '@/components/OpportunityBanner';

const BANNERS_LS_KEY = 'loom_banners_quiet';
const BANNERS_EVENT  = 'loom:banners-quiet';
const COLUMN_WIDTH_LS_KEY = 'loom_editor_column_width';
export const COLUMN_WIDTH_EVENT = 'loom:editor-column-width';
// Info strip (status bar) visibility key — kept in sync with AppShell
const STATUSBAR_LS_KEY = 'loom_statusbar_visible';
const STATUSBAR_EVENT  = 'loom:statusbar-visible';

const COLUMN_WIDTH_OPTIONS = [
  { value: 'full',  label: 'Full width',   hint: 'Fills the entire editor area' },
  { value: '900',   label: 'Wide (900px)', hint: 'Comfortable for large screens' },
  { value: '780',   label: 'Medium (780px)', hint: 'Classic novel-layout width' },
  { value: '680',   label: 'Narrow (680px)', hint: 'Focused single-column reading' },
];

export function readColumnWidth() {
  return localStorage.getItem(COLUMN_WIDTH_LS_KEY) || 'full';
}

function QuietModeToggle() {
  const [quiet, setQuiet] = React.useState(
    () => localStorage.getItem(BANNERS_LS_KEY) !== '0',
  );
  const toggle = (v) => {
    setQuiet(v);
    localStorage.setItem(BANNERS_LS_KEY, v ? '1' : '0');
    window.dispatchEvent(new CustomEvent(BANNERS_EVENT));
  };
  return (
    <Switch
      checked={quiet}
      onCheckedChange={toggle}
      data-testid="settings-quiet-mode-toggle"
    />
  );
}

function StatusBarToggle() {
  const [visible, setVisible] = React.useState(
    () => localStorage.getItem(STATUSBAR_LS_KEY) !== '0',
  );
  const toggle = (v) => {
    setVisible(v);
    localStorage.setItem(STATUSBAR_LS_KEY, v ? '1' : '0');
    window.dispatchEvent(new CustomEvent(STATUSBAR_EVENT));
  };
  return (
    <Switch
      checked={visible}
      onCheckedChange={toggle}
      data-testid="settings-statusbar-toggle"
    />
  );
}

function PromptModePicker() {
  // Read once on mount; changing it re-renders the whole app because
  // every CoachHint reads `getPromptMode()` on render. That's cheap
  // enough that we don't need to plumb state into a context.
  const [mode, setMode] = React.useState(() => getPromptMode() || 'off');
  return (
    <Select
      value={mode}
      onValueChange={(v) => { setPromptMode(v); setMode(v); }}
    >
      <SelectTrigger className="h-8 w-40 text-[12px]" data-testid="settings-prompt-mode-trigger">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="guided" data-testid="settings-prompt-mode-guided">Guided</SelectItem>
        <SelectItem value="light"  data-testid="settings-prompt-mode-light">Light</SelectItem>
        <SelectItem value="off"    data-testid="settings-prompt-mode-off">Off</SelectItem>
      </SelectContent>
    </Select>
  );
}

// iter-reask-freq · Sep-2026 — "How often should we ask again?" picker.
// Only meaningful while the coach-prompt mode isn't "Off" (the picker
// never shows at all in that case), so it's disabled otherwise.
function ReaskFrequencyDropdown() {
  const [freq, setFreq] = React.useState(() => getReaskFrequency());
  const [mode, setMode] = React.useState(() => getPromptMode() || 'off');
  return (
    <Select
      value={freq}
      disabled={mode === 'off'}
      onValueChange={(v) => { setReaskFrequency(v); setFreq(v); }}
      onOpenChange={() => setMode(getPromptMode() || 'off')}
    >
      <SelectTrigger className="h-8 w-40 text-[12px]" data-testid="settings-reask-frequency-trigger">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="every_login" data-testid="settings-reask-frequency-every-login">Every login</SelectItem>
        <SelectItem value="monthly"     data-testid="settings-reask-frequency-monthly">Once a month</SelectItem>
      </SelectContent>
    </Select>
  );
}

const SHORTCUTS = [
  { label: 'Commit & Rebuild',   keys: 'Ctrl + Enter' },
  { label: 'Ask AI',             keys: 'Ctrl + Space' },
  { label: 'Toggle Focus Mode',  keys: 'F11' },
  { label: 'Next Chapter',       keys: 'Ctrl + \u2192' },
  { label: 'Previous Chapter',   keys: 'Ctrl + \u2190' },
  { label: 'Open Command Palette', keys: 'Ctrl + K' },
  { label: 'Toggle Theme',       keys: 'Ctrl + Shift + L' },
  { label: 'Save Project',       keys: 'Ctrl + S' },
];

const THEME_OPTIONS = [
  { id: 'light',  label: 'Light',  Icon: Sun,     desc: 'Manuscript paper' },
  { id: 'dark',   label: 'Dark',   Icon: Moon,    desc: 'Studio default' },
  { id: 'system', label: 'System', Icon: Monitor, desc: 'Follow your OS' },
];

function ColumnWidthPicker() {
  const [value, setValue] = React.useState(() => readColumnWidth());
  const pick = (v) => {
    setValue(v);
    localStorage.setItem(COLUMN_WIDTH_LS_KEY, v);
    window.dispatchEvent(new CustomEvent(COLUMN_WIDTH_EVENT, { detail: v }));
  };
  return (
    <Select value={value} onValueChange={pick}>
      <SelectTrigger className="h-8 w-44 text-[12px]" data-testid="settings-column-width-trigger">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {COLUMN_WIDTH_OPTIONS.map((o) => (
          <SelectItem key={o.value} value={o.value} data-testid={`settings-column-width-${o.value}`}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function SettingsDialog({ open, onOpenChange, onBuyTokens, onOpenInbox, initialTab }) {
  const { themeMode, setThemeMode, project } = usePW();
  const { user } = useAuth();
  const [helpMode, setHelpMode] = React.useState(() => readHelpMode());
  // iter-p2-g — Public Profile tab opens the extracted UserProfilePanel
  // Dialog. We close Settings while the profile editor is open so the
  // two dialogs don't stack visually.
  const [profileOpen, setProfileOpen] = React.useState(false);
  // 2026-10 — Deep-link support: callers (e.g. the BYOK paywall nudge)
  // can open Settings directly on a given tab via `initialTab`. Only
  // re-synced when the dialog transitions to open so a user manually
  // switching tabs afterward isn't yanked back.
  const [tab, setTab] = React.useState(initialTab || 'account');
  React.useEffect(() => { if (open) setTab(initialTab || 'account'); }, [open, initialTab]);
  // Analytics tab handled by ClaritySettingsCard (self-contained)

  // ── Opportunity banner preference ────────────────────────────────────────
  const [opportunityBannerHidden, setOpportunityBannerHidden] = useState(readOpportunityBannerPref);
  const toggleOpportunityBanner = (v) => {
    writeOpportunityBannerPref(!v);
    setOpportunityBannerHidden(!v);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden">
        <DialogHeader className="border-b border-border bg-panel-header/40 px-5 py-3">
          <DialogTitle className="flex items-center gap-2 text-[14px]"><Cog className="h-4 w-4 text-primary" /> Preferences</DialogTitle>
          <DialogDescription className="text-[11.5px]">Account, appearance, autosave, AI behavior, and shortcuts.</DialogDescription>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab} className="flex h-[460px]">
          <TabsList className="flex h-full w-48 flex-col items-stretch justify-start gap-0 rounded-none border-r border-border bg-panel-header/20 p-2">
            <TabsTrigger value="account"    className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="settings-tab-account"><UserCircle className="h-3.5 w-3.5" /> My Account</TabsTrigger>
            <TabsTrigger value="profile"    className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="settings-tab-profile"><IdCard className="h-3.5 w-3.5" /> Public Profile</TabsTrigger>
            <TabsTrigger value="appearance" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="settings-tab-appearance"><Sun className="h-3.5 w-3.5" /> Appearance</TabsTrigger>
            <TabsTrigger value="reading"    className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="settings-tab-reading"><BookOpen className="h-3.5 w-3.5" /> Reading</TabsTrigger>
            <TabsTrigger value="ai"         className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background"><ShieldCheck className="h-3.5 w-3.5" /> AI Model</TabsTrigger>
            <TabsTrigger value="billing"    className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background"><CreditCard className="h-3.5 w-3.5" /> Billing</TabsTrigger>
            <TabsTrigger value="connections" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="settings-tab-connections"><LinkIcon className="h-3.5 w-3.5" /> Connections</TabsTrigger>
            <TabsTrigger value="language"   className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="settings-tab-language"><Languages className="h-3.5 w-3.5" /> Language</TabsTrigger>
            <TabsTrigger value="files"      className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background"><FolderOpen className="h-3.5 w-3.5" /> Files &amp; Backups</TabsTrigger>
            <TabsTrigger value="keys"       className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background"><Keyboard className="h-3.5 w-3.5" /> Shortcuts</TabsTrigger>
            <TabsTrigger value="analytics"  className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="settings-tab-analytics"><BarChart2 className="h-3.5 w-3.5" /> Analytics</TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-auto scroll-thin px-6 py-5">
            <TabsContent value="account" className="m-0">
              <MyAccountPanel onBuyTokens={() => { onOpenChange(false); onBuyTokens?.(); }} />
            </TabsContent>

            {/* iter-p2-g — Public Profile tab. Renders a compact summary
                of what the /authors/<slug> page will show, plus a
                CTA that hands off to the full UserProfilePanel Dialog.
                Settings closes while the editor is open — see
                onOpenChange handoff below. */}
            <TabsContent value="profile" className="m-0 space-y-4" data-testid="settings-profile-tab">
              <section>
                <h3 className="mb-1 text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Public author profile
                </h3>
                <p className="text-[11.5px] text-muted-foreground">
                  Your pen name, portrait, short bio, and opt-ins for the
                  landing Author Ticket Rail, fan-contact inbox, and free
                  sample chapter — all in one editor. Everything you save
                  here is what readers see on <code>/authors/&lt;your-slug&gt;</code>.
                </p>
              </section>
              <ul className="grid gap-1.5 text-[11.5px] text-muted-foreground">
                <li>· Pen name (auto-slugs your public URL)</li>
                <li>· Portrait upload or paste URL</li>
                <li>· 280-character bio pitch</li>
                <li>· Show on the landing Author Rail</li>
                <li>· Accept fan messages (opens a dedicated inbox)</li>
                <li>· Offer a free sample chapter</li>
              </ul>
              <div>
                <Button
                  size="sm"
                  onClick={() => { setProfileOpen(true); onOpenChange(false); }}
                  data-testid="settings-open-profile-editor"
                  className="gap-1.5"
                >
                  <Edit3 className="h-3.5 w-3.5" /> Open profile editor
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="appearance" className="m-0 space-y-6">
              <section>
                <h3 className="mb-2 text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">Theme</h3>
                <div className="grid grid-cols-3 gap-2">
                  {THEME_OPTIONS.map((o) => (
                    <ThemeCard key={o.id} option={o} active={themeMode === o.id} onPick={setThemeMode} />
                  ))}
                </div>
              </section>
              <Row label="Accent intensity" hint="How saturated the amber accent appears."><Slider defaultValue={[68]} /></Row>
              <Row label="UI font size" hint="Affects panels and menus, not the manuscript.">
                <Select defaultValue="comfortable">
                  <SelectTrigger className="h-8 w-48 text-[12px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="compact">Compact</SelectItem>
                    <SelectItem value="comfortable">Comfortable</SelectItem>
                    <SelectItem value="spacious">Spacious</SelectItem>
                  </SelectContent>
                </Select>
              </Row>
              <Row label="Reduce motion" hint="Disable subtle animations"><Switch /></Row>
              <Row
                label="Editor column width"
                hint="Maximum width of the manuscript writing area. 'Full width' fills the panel."
              >
                <ColumnWidthPicker />
              </Row>
              {/* iter55z-o — Contextual tool help toggle. */}
              <Row
                label="Contextual help popups"
                hint="Hover any tool for a hint; right-click to pin a larger help panel."
              >
                <Switch
                  checked={helpMode}
                  onCheckedChange={(v) => { writeHelpMode(!!v); setHelpMode(!!v); }}
                  data-testid="settings-help-mode-toggle"
                />
              </Row>
              {/* ── Reader-interest alert banner toggle ── */}
              <Row
                label="Reader interest alerts"
                hint="Shows a slim banner when a genre is trending with high reader demand. You can also dismiss it per-session with the × on the banner itself."
              >
                <Switch
                  checked={!opportunityBannerHidden}
                  onCheckedChange={toggleOpportunityBanner}
                  data-testid="settings-opportunity-banner-toggle"
                />
              </Row>
              {/* iter55z-p — Replay the first-run walkthrough. */}
              <Row
                label="Replay guided tour"
                hint="Walk through Search → Bookmarks → Notes → Export again."
              >
                <button
                  type="button"
                  onClick={() => { resetTour(); onOpenChange(false); }}
                  data-testid="settings-restart-tour"
                  className="inline-flex items-center gap-1.5 rounded-sm border border-border bg-background px-3 py-1 text-[11.5px] hover:bg-secondary"
                >
                  Start tour
                </button>
              </Row>
              {/* iter55z-q — Reset every contextual mini-tour so they replay. */}
              <Row
                label="Reset feature tours"
                hint="Character Bible, Story Bible, Suggestions, Unit Economics, and My Billing tours will replay on next visit."
              >
                <button
                  type="button"
                  onClick={() => {
                    ['character-bible-open','story-bible-open','suggestions-first-open',
                     'unit-economics-open','my-billing-open'].forEach((id) => resetTour(id));
                    onOpenChange(false);
                  }}
                  data-testid="settings-reset-feature-tours"
                  className="inline-flex items-center gap-1.5 rounded-sm border border-border bg-background px-3 py-1 text-[11.5px] hover:bg-secondary"
                >
                  Reset all
                </button>
              </Row>
              {/* iter55f81 — Weekly Notebook Digest opt-in. */}
              <Row
                label="Weekly Notebook Digest email"
                hint="Every Monday morning, get a digest of the notebook jots you captured over the past 7 days, grouped by project."
              >
                <NotebookDigestToggle />
              </Row>
              {/* iter55f84 — Change the "what to do next" coach prompt mode. */}
              <Row
                label={'"What to do next" coach prompts'}
                hint="Hover any writing surface for a mentor-voice tip on what to try next. Guided also shows tool tooltips; Light shows only the coach hints; Off hides everything."
              >
                <PromptModePicker />
              </Row>
              {/* iter-reask-freq · Sep-2026 — how often the Guided/Light/Off
                  picker itself re-asks on login. */}
              <Row
                label="Ask my guidance level again"
                hint="The 'How much guidance would you like?' popup can re-ask every time you log in, or just once a month. Has no effect while coach prompts are Off."
              >
                <ReaskFrequencyDropdown />
              </Row>
              {/* Quiet Mode — hide marketing banners in the Loom ── */}
              <Row
                label="Quiet mode"
                hint="Hide promotional banners in the Loom. Same switch as Tools → Quiet Mode — both stay in sync."
              >
                <QuietModeToggle />
              </Row>
              {/* Info strip — the compact status bar moved below title bar */}
              <Row
                label="Show info strip"
                hint="The compact strip below the title bar showing word count, tokens, AI model, and cache status. Can also be dismissed with the × button on the strip."
              >
                <StatusBarToggle />
              </Row>
            </TabsContent>

            <TabsContent value="reading" className="m-0 space-y-5" data-testid="settings-reading-tab">
              <ReadingPrefsPanel />
            </TabsContent>

            <TabsContent value="ai" className="m-0 space-y-5">
              <ModelPicker />
              <Row label="Reference memory" hint="How much prior text the model considers.">
                <Select defaultValue="full">
                  <SelectTrigger className="h-8 w-48 text-[12px]" data-testid="settings-memory-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Last chapter</SelectItem>
                    <SelectItem value="3">Last 3 chapters</SelectItem>
                    <SelectItem value="full">Full novel</SelectItem>
                  </SelectContent>
                </Select>
              </Row>
              <Row label="Real-time AI suggestions" hint="Stream completions as you type."><Switch defaultChecked /></Row>
              <Row label="Confirm before committing" hint="Show a diff before rebuilding the novel."><Switch defaultChecked /></Row>
              {/* Provider API keys (BYOK) */}
              <div className="pt-1">
                <div className="mb-2 text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Your Provider API Keys
                </div>
                <ByokKeysPanel />
              </div>
            </TabsContent>

            <TabsContent value="billing" className="m-0 space-y-5">
              <MyBillingPanel
                onChoosePlan={() => {
                  // Same event the axios 402 interceptor uses (PaywallProvider).
                  window.dispatchEvent(new CustomEvent('paywall:open', {
                    detail: { title: 'Choose a plan', tab: 'subscribe' },
                  }));
                }}
              />
              <BillingPanel />
            </TabsContent>

            <TabsContent value="connections" className="m-0 space-y-5">
              <ConnectionsPanel />
            </TabsContent>

            <TabsContent value="language" className="m-0 space-y-5" data-testid="settings-language-tab">
              <ProjectLanguagePanel project={project} />
              {onOpenInbox && (
                <div className="rounded-md border border-border bg-panel-header/20 p-3">
                  <div className="mb-1 text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">Your inbox</div>
                  <p className="mb-2 text-[11.5px] text-muted-foreground">
                    Replies from readers, editors, and support land in your personal inbox — routed to your unique <code>@mail</code> alias.
                  </p>
                  <button
                    type="button"
                    onClick={() => { onOpenChange(false); onOpenInbox(); }}
                    data-testid="settings-open-inbox"
                    className="inline-flex items-center gap-1.5 rounded-sm border border-border bg-background px-3 py-1 text-[11.5px] hover:bg-secondary"
                  >
                    <Inbox className="h-3.5 w-3.5" /> Open My Inbox
                  </button>
                </div>
              )}
            </TabsContent>

            <TabsContent value="files" className="m-0 space-y-5">
              <Row label="Auto-save interval" hint="Saves to project folder.">
                <Select defaultValue="60"><SelectTrigger className="h-8 w-48 text-[12px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">Every 30 seconds</SelectItem>
                    <SelectItem value="60">Every 1 minute</SelectItem>
                    <SelectItem value="300">Every 5 minutes</SelectItem>
                  </SelectContent>
                </Select>
              </Row>
              <Row label="Version branches retained" hint="Maximum revision snapshots kept."><Slider defaultValue={[10]} max={50} min={1} /></Row>
              <Row label="Backup to OneDrive" hint="Mirror project folder to cloud."><Switch defaultChecked /></Row>
            </TabsContent>

            <TabsContent value="keys" className="m-0">
              <div className="rounded-md border border-border">
                {SHORTCUTS.map(({ label, keys }) => (
                  <div key={label} className="flex items-center justify-between border-b border-border px-3 py-2 last:border-b-0">
                    <span className="text-[12px]">{label}</span>
                    <kbd className="rounded-sm border border-border bg-background px-1.5 py-0.5 font-mono-stat text-[10.5px] text-foreground">{keys}</kbd>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="analytics" className="m-0 space-y-5" data-testid="settings-analytics-tab">
              <div className="space-y-1">
                <h3 className="text-[13px] font-semibold text-foreground">Page Analytics</h3>
                <p className="text-[11.5px] text-muted-foreground">
                  Connect a free Microsoft Clarity account to track how readers interact
                  with <strong>your</strong> book pages — heatmaps, scroll depth, session
                  recordings. Only your pages, only your data.
                </p>
              </div>
              <ClaritySettingsCard />
            </TabsContent>
          </div>
        </Tabs>
        <DialogFooter className="border-t border-border bg-panel-header/40 px-5 py-3">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" onClick={() => { toast.success('Preferences saved'); onOpenChange(false); }}>Apply</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <UserProfilePanel open={profileOpen} onOpenChange={setProfileOpen} />
    </>
  );
}

function Row({ label, hint, children }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex-1">
        <Label className="text-[12.5px]">{label}</Label>
        {hint && <p className="mt-0.5 text-[10.5px] text-muted-foreground">{hint}</p>}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

function ThemeCard({ option, active, onPick }) {
  const Icon = option.Icon;
  return (
    <button
      onClick={() => onPick(option.id)}
      className={cn(
        'flex flex-col items-start gap-1 rounded-md border p-3 text-left transition-all',
        active ? 'border-primary bg-primary/5 shadow-panel-active' : 'border-border bg-card hover:border-primary/50'
      )}
    >
      <Icon className={cn('h-4 w-4', active ? 'text-primary' : 'text-muted-foreground')} />
      <div className="text-[12.5px] font-semibold">{option.label}</div>
      <div className="text-[10.5px] text-muted-foreground">{option.desc}</div>
    </button>
  );
}

// ── Global reading preferences ───────────────────────────────────────────────
export const LOOM_READING_LS = 'loom:reading_prefs';

export function readReadingPrefs() {
  try { return JSON.parse(localStorage.getItem(LOOM_READING_LS) || '{}'); } catch { return {}; }
}

function applyReadingPrefs(prefs) {
  const root = document.documentElement;
  // Always apply — the previous boolean guards prevented CSS vars from being
  // reset when the user toggled an option OFF (e.g. `if (prefs.boldBody)`
  // never ran when boldBody was false, so the variable kept its old value).
  root.style.setProperty('--loom-reading-size',   (prefs.bodySize ?? 14) + 'px');
  root.style.setProperty('--loom-heading-weight', prefs.boldHeaders ? '700' : '600');
  root.style.setProperty('--loom-body-weight',    prefs.boldBody    ? '600' : '400');

  // Inject / update a <style> tag so the CSS variables are actually consumed
  // by prose paragraphs.  Both the main draft editor paragraphs
  // (`[data-testid^="manuscript-para-"]`) and AI analysis panel text
  // (`[data-loom-analysis] p`) react to the variables — no other component
  // changes are required.
  let el = document.getElementById('__loom_reading_prefs__');
  if (!el) {
    el = document.createElement('style');
    el.id = '__loom_reading_prefs__';
    document.head.appendChild(el);
  }
  el.textContent = [
    /* Main draft editor paragraphs */
    `[data-testid^="manuscript-para-"] {`,
    `  font-size: var(--loom-reading-size) !important;`,
    `  font-weight: var(--loom-body-weight) !important;`,
    `}`,
    /* AI analysis panel text (Arc Walker, Transitions, Plot Readiness…) */
    `[data-loom-analysis] p,`,
    `[data-loom-analysis] li,`,
    `[data-loom-analysis] .prose-body {`,
    `  font-size: var(--loom-reading-size) !important;`,
    `  font-weight: var(--loom-body-weight) !important;`,
    `}`,
    `[data-loom-analysis] h3,`,
    `[data-loom-analysis] h4,`,
    `[data-loom-analysis] strong {`,
    `  font-weight: var(--loom-heading-weight) !important;`,
    `}`,
  ].join('\n');
}

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

/* Feb-2026 — Genre slugs shown in the preference picker.
 * Ordered by popularity on the platform. */
const GENRE_OPTIONS = [
  { slug: 'dark-romance',      label: 'Dark Romance' },
  { slug: 'romance',           label: 'Romance' },
  { slug: 'fantasy',           label: 'Fantasy' },
  { slug: 'gothic-horror',     label: 'Gothic Horror' },
  { slug: 'sci-fi',            label: 'Sci-Fi' },
  { slug: 'thriller',          label: 'Thriller' },
  { slug: 'mystery',           label: 'Mystery' },
  { slug: 'historical-fiction',label: 'Historical Fiction' },
  { slug: 'paranormal',        label: 'Paranormal' },
  { slug: 'nonfiction',        label: 'Non-Fiction' },
];

function GenrePreferencesPicker() {
  const [picks, setPicks]     = useState(null); // null = loading
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    axios.get(`${API}/user/genre-preferences`, { withCredentials: true })
      .then(r => setPicks(r.data?.explicit_picks || []))
      .catch(() => setPicks([]));
  }, []);

  const toggle = async (slug) => {
    if (picks === null) return;
    const next = picks.includes(slug)
      ? picks.filter(s => s !== slug)
      : [...picks, slug];
    setPicks(next);
    setSaving(true);
    try {
      await axios.put(`${API}/user/genre-preferences/picks`,
        { picks: next }, { withCredentials: true });
      toast.success('Genre preferences saved.');
    } catch {
      toast.error('Could not save genre preferences.');
    } finally { setSaving(false); }
  };

  if (picks === null) return (
    <div className="text-[11px] text-muted-foreground py-2">Loading…</div>
  );

  return (
    <div className="space-y-3" data-testid="genre-preferences-picker">
      <div>
        <h3 className="mb-1 text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">
          Bookstore genre preferences
        </h3>
        <p className="text-[11.5px] text-muted-foreground">
          Pin the genres you write or enjoy reading. The bookstore carousel will surface these
          shelves first, and your project genres are also factored in automatically.
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        {GENRE_OPTIONS.map(g => {
          const active = picks.includes(g.slug);
          return (
            <button
              key={g.slug}
              onClick={() => toggle(g.slug)}
              disabled={saving}
              data-testid={`genre-pick-${g.slug}`}
              className={[
                'rounded-full border px-3 py-1 text-[12px] font-medium transition-colors',
                active
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-muted text-muted-foreground hover:border-primary/60 hover:text-foreground',
              ].join(' ')}
            >
              {g.label}
            </button>
          );
        })}
      </div>
      {picks.length > 0 && (
        <p className="text-[11px] text-muted-foreground">
          {picks.length} genre{picks.length !== 1 ? 's' : ''} pinned — the carousel will prioritise these.
        </p>
      )}
    </div>
  );
}

function ReadingPrefsPanel() {
  const [prefs, setPrefs] = React.useState(() => ({
    bodySize:    14,
    boldHeaders: false,
    boldBody:    false,
    ...readReadingPrefs(),
  }));

  const save = (updates) => {
    const next = { ...prefs, ...updates };
    setPrefs(next);
    localStorage.setItem(LOOM_READING_LS, JSON.stringify(next));
    applyReadingPrefs(next);
    toast.success('Reading preferences saved');
  };

  // Apply on mount
  React.useEffect(() => { applyReadingPrefs(prefs); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-5" data-testid="reading-prefs-panel">
      <section>
        <h3 className="mb-1 text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">
          Analysis panel reading
        </h3>
        <p className="text-[11.5px] text-muted-foreground">
          Controls the default text size and weight for AI analysis panels (Arc Walker, Plot Readiness, Continuity Review, etc.).
          Individual panels also have per-panel overrides in their header.
        </p>
      </section>
      <Row label="Body text size" hint="Base font size for analysis and result text.">
        <Select
          value={String(prefs.bodySize)}
          onValueChange={(v) => save({ bodySize: Number(v) })}
        >
          <SelectTrigger className="h-8 w-40 text-[12px]" data-testid="reading-body-size-trigger">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="12" data-testid="reading-size-12">12 px — Compact</SelectItem>
            <SelectItem value="14" data-testid="reading-size-14">14 px — Default</SelectItem>
            <SelectItem value="16" data-testid="reading-size-16">16 px — Comfortable</SelectItem>
            <SelectItem value="18" data-testid="reading-size-18">18 px — Large</SelectItem>
          </SelectContent>
        </Select>
      </Row>
      <Row label="Bold headings" hint="Heavier weight on section labels and character names in analysis panels.">
        <Switch
          checked={!!prefs.boldHeaders}
          onCheckedChange={(v) => save({ boldHeaders: v })}
          data-testid="reading-bold-headers-toggle"
        />
      </Row>
      <Row label="Bold body text" hint="Heavier weight on body / analysis prose text.">
        <Switch
          checked={!!prefs.boldBody}
          onCheckedChange={(v) => save({ boldBody: v })}
          data-testid="reading-bold-body-toggle"
        />
      </Row>
      <div className="rounded-md border border-border bg-panel-header/20 p-3">
        <p className="mb-2 text-[11.5px] font-semibold text-foreground">Preview</p>
        <p
          style={{
            fontSize: `${prefs.bodySize}px`,
            fontWeight: prefs.boldBody ? 600 : 400,
          }}
          className="leading-relaxed text-foreground"
        >
          The Arc Walker found 3 character arcs that need attention. Rising Action is weak in chapters 4–7.
        </p>
        <p
          style={{
            fontSize: `${prefs.bodySize + 1}px`,
            fontWeight: prefs.boldHeaders ? 700 : 600,
          }}
          className="mt-2 text-foreground"
        >
          Section heading example
        </p>
      </div>
      {/* Feb-2026 — Bookstore genre preferences */}
      <div className="border-t border-border pt-4">
        <GenrePreferencesPicker />
      </div>
    </div>
  );
}


// English, Spanish, Arabic, Chinese first; expose the four codes
// backed by the /api/projects/languages/supported catalogue.
const LANGUAGE_CHOICES = [
  { code: 'en', label: 'English',              rtl: false },
  { code: 'es', label: 'Spanish (Español)',    rtl: false },
  { code: 'ar', label: 'Arabic (العربية)',     rtl: true  },
  { code: 'zh', label: 'Chinese (中文)',        rtl: false },
];

function ProjectLanguagePanel({ project }) {
  const initial = (project && project.output_language) || 'en';
  const [value, setValue] = React.useState(initial);
  const [busy, setBusy]   = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  React.useEffect(() => { setValue(initial); }, [initial]);
  const meta = LANGUAGE_CHOICES.find((l) => l.code === value) || LANGUAGE_CHOICES[0];
  const save = async (code) => {
    if (!project?.id) {
      toast.error('Open a project first before changing its language.');
      return;
    }
    setBusy(true);
    setSaved(false);
    try {
      await projectsApi.setLanguage(project.id, code);
      setValue(code);
      setSaved(true);
      toast.success(`Manuscript language set to ${LANGUAGE_CHOICES.find((l) => l.code === code)?.label || code}. New chapter generations will follow this language.`);
      setTimeout(() => setSaved(false), 3200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update project language.');
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="project-language-panel">
      <section>
        <h3 className="mb-1 text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">
          Manuscript output language
        </h3>
        <p className="text-[11.5px] text-muted-foreground">
          Choose the language Claude, Gemini, and OpenAI will use for
          every new chapter, synopsis, opener, and cliffhanger it
          drafts for <b>{project?.name || 'this project'}</b>. Existing
          text is not translated — only new generations follow the
          new language.
        </p>
      </section>
      <div className="flex items-center gap-3">
        <Label className="text-[12.5px]">Language</Label>
        <Select value={value} onValueChange={save} disabled={busy || !project?.id}>
          <SelectTrigger className="h-9 w-64 text-[12.5px]" data-testid="project-language-trigger">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {LANGUAGE_CHOICES.map((l) => (
              <SelectItem key={l.code} value={l.code} data-testid={`project-language-option-${l.code}`}>
                {l.label}
                {l.rtl && <span className="ml-2 text-[10.5px] text-muted-foreground">RTL</span>}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {busy && <span className="text-[11px] text-muted-foreground">Saving…</span>}
        {saved && <span className="text-[11px] text-emerald-600" data-testid="project-language-saved">Saved</span>}
      </div>
      {meta?.rtl && (
        <div className="rounded-md border border-amber-400/40 bg-amber-50/60 p-3 text-[11.5px] text-amber-900" data-testid="project-language-rtl-note">
          {meta.label} is a right-to-left script. Your manuscript view
          will render right-aligned with the Noto Naskh Arabic
          fallback for glyphs your primary manuscript font does not
          support.
        </div>
      )}
      {(value === 'zh' || value === 'ja') && (
        <div className="rounded-md border border-sky-400/40 bg-sky-50/60 p-3 text-[11.5px] text-sky-900" data-testid="project-language-cjk-note">
          CJK glyphs will fall back to Noto Serif SC / JP where your
          primary font does not include coverage — no missing-glyph
          boxes.
        </div>
      )}
      <div className="rounded-md border border-border bg-panel-header/20 p-3 text-[11px] text-muted-foreground">
        Additional languages (French, German, Portuguese, Japanese,
        Korean, Russian, Hindi, and 15 more) are available via the
        backend catalog and can be added to this dropdown on request.
      </div>
    </div>
  );
}

